import useVuelidate from '@vuelidate/core';
import { helpers } from '@vuelidate/validators';
import Menu from 'takeorder-menu-js';
import { mapActions, mapState } from 'vuex';

import MenuCommonDialog from '@/components/Store/MenuCommonDialog/MenuCommonDialog.vue';
import AddMultipleMenuItems from '@/components/Store/AddMultipleMenuItems/AddMultipleMenuItems.vue';
import { MenuItemsEditor } from '@/components';
import bus from '@/utils/bus';
import { ORDER_STATUS_WITH_TYPE_TEXT, SNACKBAR_TYPES } from '@/utils/constants';
import { required } from '@/utils/validators';

const CREATE = 'create';
const GROUP_BUYINGS = 'group_buyings';

export default {
  name: 'StoreMenus',
  components: {
    AddMultipleMenuItems,
    MenuCommonDialog,
    MenuItemsEditor,
  },
  setup() {
    return { v$: useVuelidate({ $lazy: true }) };
  },
  data() {
    return {
      categories: [],
      entrees: [],
      inEdit: false,
      menuName: '',
      saved: false,
      showAddMultiple: false,
      showOnDeleteMenuDialog: false,
      showOnLeaveDialog: false,
    };
  },
  beforeRouteLeave(to, from, next) {
    if (!this.showOnLeaveDialog && !this.saved) {
      this.showOnLeaveDialog = true;
      return;
    }
    next();
  },
  beforeDestroy() {
    window.removeEventListener('beforeunload', this.preventNav);
    Object.assign(this.$data, this.$options.data.apply(this));
    this.setMenu({});
  },
  async created() {
    const { action } = this.$route?.params || {};
    if (action && action !== CREATE) {
      this.inEdit = true;
    }

    if (this.inEdit) {
      await this.getMenuData();
      this.setMenu(Menu.build({ input: this.rawMenu }));
      this.menuName = this.rawMenu?.meta?.name || '';
    } else {
      this.setEmptyMenu();
    }
    this.categories = this.menu?.categories || [];
    this.entrees = this.menu?.entrees || [];
  },
  mounted() {
    window.addEventListener('beforeunload', this.preventNav);
    window.scrollTo({ top: 0, behavior: 'smooth' });
    this.$nextTick(() => {
      this.$refs.menuName.focus();
    });
  },
  computed: {
    ...mapState('menu', [
      'isCreatingMenu',
      'isDeletingMenu',
      'isGettingMenu',
      'isUpdatingMenu',
      'menu',
      'rawMenu',
    ]),
    isGroupBuyings() {
      const { action } = this.$route?.params || {};
      return action && action === GROUP_BUYINGS;
    },
    errorCategoriesMessages() {
      return this.v$?.categories?.$each?.$message || [];
    },
    errorText() {
      const errors = {
        菜單名稱: !!this.v$?.menuName?.$invalid,
        品項名稱及價格: !this.isValidEntrees,
        類別名稱: !!this.hasInvalidCategories,
      };
      const errorsText = Object.keys(errors).filter((key) => errors[key]).join('、');
      return `${errorsText}尚未填寫完整！`;
    },
    hasInvalidCategories() {
      return this.v$?.categories?.$invalid;
    },
    isValidEntrees() {
      return this.entrees?.every(({ name, price }) => name && price) && !!this.entrees?.length;
    },
    isLoading() {
      return this.isCreatingMenu || this.isUpdatingMenu || this.isDeletingMenu;
    },
    menuNameErrorMsg() {
      return this.v$.menuName?.$errors?.[0]?.$message || '';
    },
    showAddCategoryBtn() {
      return this.categories?.every(({ name }) => name) && !!this.categories?.length;
    },
  },
  methods: {
    ...mapActions('menu', {
      createMenu: 'CREATE_MENU',
      deleteMenu: 'DELETE_MENU',
      getMenu: 'GET_MENU',
      setMenu: 'SET_MENU',
      updateMenu: 'UPDATE_MENU',
      getGroupBuyingsMenu: 'GET_GROUP_BUYINGS_MENU',
      updateGroupBuyingsMenu: 'UPDATE_GROUP_BUYINGS_MENU',
    }),
    ...mapActions('orders', {
      getOrderMenu: 'GET_ORDER_MENU',
    }),
    backToMainPage() {
      if (this.isGroupBuyings) {
        const { query: { tab } = {} } = this.$route || {};
        const isValidTab = Object.values(ORDER_STATUS_WITH_TYPE_TEXT).includes(tab);
        const query = isValidTab ? { tab } : {};
        this.$router.push({ name: 'OrderList', query });
        return;
      }
      const { storeId } = this.$route?.params || {};
      this.$router.push({ path: `/store/${storeId}/edit` });
    },
    async getMenuData() {
      try {
        if (this.isGroupBuyings) {
          const { order_id: orderId } = this.$route?.query || {};
          await this.getGroupBuyingsMenu(orderId);
        } else {
          const { action: menuId, storeId } = this.$route?.params || {};
          await this.getMenu({ storeId, menuId });
        }
      } catch (error) {
        const { message } = error;
        const errorMessage = message ? `（錯誤：${message}）` : '';

        this.$snackbar({
          type: SNACKBAR_TYPES.ERROR,
          title: '取得菜單失敗',
          msg: `取得菜單失敗，請稍後再試。${errorMessage}`,
        });
      }
    },
    onContinueEdit() {
      this.showOnLeaveDialog = false;
    },
    async onCreate() {
      const valid = await this.v$.$validate();
      if (!valid) {
        return;
      }
      try {
        this.setMenuName();
        const { storeId } = this.$route?.params || {};
        await this.createMenu({ storeId, menu: this.menu.toJson() });
        this.saved = true;
        this.backToMainPage();
      } catch (error) {
        const { message } = error;
        const errorMessage = message ? `（錯誤：${message}）` : '';

        this.$snackbar({
          type: SNACKBAR_TYPES.ERROR,
          title: '建立菜單失敗',
          msg: `建立菜單失敗，請稍後再試。${errorMessage}`,
        });
      }
    },
    async onDeleteMenu() {
      try {
        const { action: menuId, storeId } = this.$route?.params || {};
        await this.deleteMenu({
          menuId,
          storeId,
        });
        this.saved = true;
        this.backToMainPage();
        this.$snackbar({ type: SNACKBAR_TYPES.SUCCESS, title: '菜單刪除成功。' });
      } catch (error) {
        const { message } = error;
        const errorMessage = message ? `（錯誤：${message}）` : '';

        this.$snackbar({
          type: SNACKBAR_TYPES.ERROR,
          title: '刪除菜單失敗',
          msg: `刪除菜單失敗，請稍後再試。${errorMessage}`,
        });
      }
    },
    onCloseDeleteMenuDialog() {
      this.showOnDeleteMenuDialog = false;
    },
    async onSave() {
      const valid = await this.v$.$validate();
      if (!valid) {
        return;
      }
      try {
        this.setMenuName();
        const { action: menuId, storeId } = this.$route?.params || {};
        if (this.isGroupBuyings) {
          const { order_id: orderId } = this.$route?.query || {};
          await this.updateGroupBuyingsMenu({ menu: this.menu.toJson(), orderId });
        } else {
          await this.updateMenu({ menu: this.menu.toJson(), menuId, storeId });
        }
        this.saved = true;
        this.backToMainPage();
      } catch (error) {
        const { message } = error;
        const errorMessage = message ? `（錯誤：${message}）` : '';

        this.$snackbar({
          type: SNACKBAR_TYPES.ERROR,
          title: '更新菜單失敗',
          msg: `更新菜單失敗，請稍後再試。${errorMessage}`,
        });
      }
    },
    preventNav(event) {
      if (!this.saved) {
        event.preventDefault();
        // eslint-disable-next-line no-param-reassign
        event.returnValue = '';
      }
    },
    replaceItems(items) {
      bus.$emit('ADD_NEW_CATEGORY_WITH_ITEMS', items);
      bus.$emit('RESET_DISPLAY_ENTREE');
    },
    setEmptyMenu() {
      const menu = new Menu({});
      this.setMenu(menu);
    },
    setMenuName() {
      this.menu.setMeta({
        name: this.menuName,
      });
    },
  },
  validations() {
    return {
      categories: {
        $each: helpers.forEach({
          _name: { required: required('類別名稱') },
        }),
      },
      menuName: {
        required: required('菜單名稱'),
      },
    };
  },
};
